'use client'
import { useState, useEffect } from 'react'

export type EntityType = 'masjid' | 'business' | 'user'

export interface SocialEntity {
  id: string; type: EntityType; name: string; slug?: string;
  area?: string; city?: string; photo?: string; plan?: string;
  verified?: boolean; followers?: number;
}

export interface ActivityItem {
  id: string; ts: number; type: 'follow_masjid' | 'follow_business' | 'donated' | 'announcement' | 'janaza' | 'campaign_update' | 'new_offer' | 'new_volunteer'
  actor: { name: string; avatar?: string; type: EntityType }
  target: { name: string; slug?: string; type: EntityType; photo?: string }
  meta?: { amount?: number; message?: string; prayerTime?: string }
}

export interface Notification {
  id: string; ts: number; read: boolean; type: string
  title: string; body: string; link?: string; photo?: string; icon?: string
}

const LS_FOLLOWS    = 'social_follows'
const LS_ACTIVITY   = 'social_activity'
const LS_NOTIFS     = 'social_notifications'
const LS_SAVED      = 'social_saved'

/* HONESTY: SEED_ACTIVITY removed — it was the feed's SOLE content and was entirely
   fabricated: an invented Janaza (death) notice with a made-up name, a fake "Ahmed K.
   donated ₹1,000", a fake business Eid offer, invented announcements and a fake volunteer
   credit. The activity feed now shows ONLY real activity the user actually generates (e.g.
   the follow item created in follow() below) — or nothing. An empty feed is honest; a feed
   of invented community events is not.

   SEED_NOTIFICATIONS was removed earlier for the same reason (an invented Janaza alert and a
   fake ₹1,000 donation receipt). Notifications also start empty and fill from real actions. */

function getLS<T>(key: string, def: T): T {
  try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : def } catch { return def }
}
function setLS(key: string, val: unknown) {
  try { localStorage.setItem(key, JSON.stringify(val)) } catch {}
}

export function useSocial() {
  const [follows,   setFollows]   = useState<SocialEntity[]>([])
  const [saved,     setSaved]     = useState<string[]>([])
  const [activity,  setActivity]  = useState<ActivityItem[]>([]) // honest-empty; fills from real user actions only
  const [notifs,    setNotifs]    = useState<Notification[]>([])
  const [ready,     setReady]     = useState(false)

  useEffect(() => {
    setFollows(getLS(LS_FOLLOWS, []))
    setSaved(getLS(LS_SAVED, []))
    // Honest empty when the user has no stored notifications — never seeded fakes.
    setNotifs(getLS<Notification[]>(LS_NOTIFS, []))
    setReady(true)
  }, [])

  const isFollowing = (id: string) => follows.some(f => f.id === id)
  const isSaved     = (id: string) => saved.includes(id)
  const unreadCount = notifs.filter(n => !n.read).length

  const follow = (entity: SocialEntity) => {
    if (isFollowing(entity.id)) return
    const next = [...follows, entity]
    setFollows(next); setLS(LS_FOLLOWS, next)
    // Add activity item
    const act: ActivityItem = { id:`a${Date.now()}`, ts:Date.now(), type:`follow_${entity.type}` as any, actor:{name:'You',type:'user'}, target:{name:entity.name,slug:entity.slug,type:entity.type,photo:entity.photo} }
    const nextAct = [act, ...activity]
    setActivity(nextAct)
    // Add notification for masjid
    if (entity.type === 'masjid') {
      const notif: Notification = { id:`n${Date.now()}`, ts:Date.now(), read:false, type:'follow', icon:'🕌', title:`Following ${entity.name}`, body:`You'll now receive announcements, Funeral prayer notices and prayer time updates.`, link:`/masjids/${entity.slug}` }
      const nextN = [notif, ...notifs]
      setNotifs(nextN); setLS(LS_NOTIFS, nextN)
    }
  }

  const unfollow = (id: string) => {
    const next = follows.filter(f => f.id !== id)
    setFollows(next); setLS(LS_FOLLOWS, next)
  }

  const toggleSave = (id: string) => {
    const next = saved.includes(id) ? saved.filter(x => x !== id) : [...saved, id]
    setSaved(next); setLS(LS_SAVED, next)
  }

  const markAllRead = () => {
    const next = notifs.map(n => ({ ...n, read:true }))
    setNotifs(next); setLS(LS_NOTIFS, next)
  }

  const markRead = (id: string) => {
    const next = notifs.map(n => n.id === id ? { ...n, read:true } : n)
    setNotifs(next); setLS(LS_NOTIFS, next)
  }

  // Get personalised feed: followed entities first, then general
  const getFeed = (filter?: 'masjid'|'business'|'all') => {
    const followedIds = new Set(follows.map(f => f.id))
    return [...activity].sort((a,b) => {
      const aFollowed = followedIds.has(a.actor.name) || followedIds.has(a.target.name)
      const bFollowed = followedIds.has(b.actor.name) || followedIds.has(b.target.name)
      if (aFollowed && !bFollowed) return -1
      if (!aFollowed && bFollowed) return 1
      return b.ts - a.ts
    }).filter(item => !filter || filter === 'all' || item.target.type === filter)
  }

  return { follows, saved, activity, notifs, ready, isFollowing, isSaved, unreadCount, follow, unfollow, toggleSave, markAllRead, markRead, getFeed }
}
